import { create } from 'zustand';
import type { ExcelData, MarkdownFile } from '../types';

// 售前能力状态管理
export const usePresalesStore = create<{
  // 状态
  excelData: ExcelData;
  markdownFiles: MarkdownFile[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  loadExcelData: () => Promise<void>;
  loadMarkdownFiles: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}>((set, get) => {
  // 初始Excel数据
  const initialExcelData: ExcelData = {
    comprehensive: {
      title: "售前综合能力",
      dimensions: ["技术理解", "需求挖掘", "方案设计", "方案演讲", "客户经营", "项目管理"],
      values: [88, 85, 80, 92, 90, 84]
    },
    technical: {
      title: "售前技术相关能力",
      dimensions: ["云计算相关", "AI相关", "网络相关", "存储相关", "云桌面相关", "云原生相关", "大数据相关", "数据库相关", "安全相关"],
      values: [90, 88, 85, 85, 92, 82, 76, 75, 78]
    }
  };

  // 获取Markdown文件
  const fetchMarkdownFiles = async (): Promise<MarkdownFile[]> => {
    try {
      const markdownFiles: MarkdownFile[] = [];
      
      // 定义要加载的Markdown文件列表
      const fileNames = [
        'AI相关.md',
        '云计算相关.md',
        '云桌面相关.md',
        '网络相关.md',
        '安全相关.md',
        '数据库相关.md',
        '大数据相关.md',
        '存储相关.md',
        '云原生相关.md'
      ];
      
      // 加载每个文件
      for (const fileName of fileNames) {
        try {
          const response = await fetch(`/data/${fileName}`);
          if (response.ok) {
            const content = await response.text();
            markdownFiles.push({
              name: fileName.replace('.md', ''),
              content,
              path: `/data/${fileName}`
            });
          }
        } catch (error) {
          console.warn(`加载文件 ${fileName} 失败:`, error);
        }
      }
      
      return markdownFiles;
    } catch (error) {
      console.error('加载Markdown文件失败:', error);
      return [];
    }
  };

  return {
    // 初始状态
    excelData: initialExcelData,
    markdownFiles: [],
    isLoading: false,
    error: null,
    
    // 加载Excel数据（当前直接返回硬编码值，避免网络请求；未来若要从Excel恢复，改回 parseExcelData()）
    loadExcelData: async () => {
      try {
        set({ isLoading: true, error: null });
        // 早期版本会 fetch /data/*.xlsx 解析，现在维度稳定后直接使用 initialExcelData
        set({ excelData: initialExcelData, isLoading: false });
      } catch (error) {
        console.error('加载能力数据失败:', error);
        set({ error: '加载能力数据失败', isLoading: false });
      }
    },
    
    // 加载Markdown文件
    loadMarkdownFiles: async () => {
      try {
        set({ isLoading: true, error: null });
        const files = await fetchMarkdownFiles();
        set({ markdownFiles: files, isLoading: false });
      } catch (error) {
        console.error('加载Markdown文件失败:', error);
        set({ error: '加载Markdown文件失败', isLoading: false });
      }
    },
    
    // 设置加载状态
    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },
    
    // 设置错误状态
    setError: (error: string | null) => {
      set({ error, isLoading: false });
    },
    
    // 重置状态
    reset: () => {
      set({
        excelData: initialExcelData,
        markdownFiles: [],
        isLoading: false,
        error: null
      });
    }
  };
});